import React from "react";
import addDays from "date-fns/addDays";
import axios from "axios";
import moment from 'moment';
import { AlertColor, Button, Link, TextField, useMediaQuery} from "@mui/material";
import { Add, Replay } from "@mui/icons-material";
import { DataGridPro, GridCellParams, GridColumns, GridToolbarContainer } from '@mui/x-data-grid-pro';
import Title from "../../component/Title";
import { BaseControl, DatePickerControl, DropDownComboControl, GenericFilter } from "../../component/GenericFilter";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { PackingListFilter } from "../../../models/classes/PackingListFilter";
import { CustomTablePagination } from "../../component/CustomPagination";
import { PackingList } from "./PackingListAccessories";
import { PackingListSingle, PackingListSingleHandle } from "./PackingListSingle";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { BranchInfo } from "../branches/BranchAccessories";
import { Pad } from "../../../common/Formatter";
import { getDefaultRegion, renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { OpenMode } from "../../../common/Enums";

export function PackingListList(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

	const singleModalRef = React.useRef<PackingListSingleHandle>(null);

    const style = { ...globalTheme() };
     
    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');
 
    const [controls, setControls] = React.useState<BaseControl[]>([]); //For filtering

    const [searchData, setSearchData] = React.useState<any>({
        contains: true,
        searchText: "",
        customer: 0,
        createdFrom: addDays(new Date(), -60),
        createdTo: new Date(),
        pageSize: 15,
    });

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	function LoadData() {
		setLoading(true);
        let filter = new PackingListFilter({
			PackingListNo: searchData.searchText,
			CustomerID: searchData.customer % 100000,
			From: moment(searchData.createdFrom).format(),
			To: moment(searchData.createdTo).format(),
			Reader: ""
		});

		axios.post<PackingList[]>('Api/V1/PackingList/Search', filter, {
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				result.data.forEach(i => {
					i.id = i.packingListNo;
				});

				setGridData(result.data.sort((a, b) => {
					if (a.createDate > b.createDate) { return -1; }
					if (a.createDate < b.createDate) { return 1; }
					if (a.createDate === b.createDate)
					{
						if (a.packingListNo > b.packingListNo) { return -1; }
						if (a.packingListNo < b.packingListNo) { return 1; }
					}
					return 0;					
				}))
				setAllData(result.data.sort((a, b) => {
					if (a.createDate > b.createDate) { return -1; }
					if (a.createDate < b.createDate) { return 1; }
					if (a.createDate === b.createDate)
					{
						if (a.packingListNo > b.packingListNo) { return -1; }
						if (a.packingListNo < b.packingListNo) { return 1; }
					}
					return 0;					
				}));
				setLoading(false);
			},
				(error) => {
					setGridData([]);
					setAllData([]);
					setLoading(false);
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();

		setControls([
			{
				key: 'createdFrom',
				title: 'Created From',
				dateValue: addDays(new Date(), -7)
			} as DatePickerControl,
			{
				key: 'createdTo',
				title: 'Created To',
				dateValue: new Date()
			} as DatePickerControl,
			{
				key: 'customer',
				title: 'Customer',
				dropdownOptions: props.customers
			} as DropDownComboControl
		]);
		LoadData();
    }

	//Grid columns
	const columns: GridColumns = [
		{
			field: 'packingListNo', headerName: 'PackingList No', flex: 1, renderCell: (params: GridCellParams) => (
				<Link
					href="#"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						EditObject(params.id.toString());
					}}
					color="inherit"
					underline="hover">
					<strong>
						{params.value}
					</strong>
				</Link>
			)
		},
		{ field: 'customerName', headerName: 'Customer', flex: 4, type: 'string' },
		{ field: 'createDate', headerName: 'Create Date', flex: 1, 
			renderCell: (params: GridCellParams) => (
				<TextField variant="standard" value={moment(params.value).format('YYYY/MM/DD')} InputProps={{ disableUnderline: true }}/>
			)
		},
		{ field: 'createdBy', headerName: 'Created By', flex: 1, type: 'string' },
	];

	const [gridData, setGridData] = React.useState<PackingList[]>([]);
    const [allData, setAllData] = React.useState<PackingList[]>([]);
	const selectedData = React.useRef<PackingList[]>([]);

	function CustomToolbar() {
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<Add />} onClick={() => NewObject()}>Create</Button>
				<div style={{ flexGrow: 1 }}></div>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<Replay />} onClick={() => Initialise()}>Reload</Button>
			</GridToolbarContainer>
		);
	}

	function NewObject() {
		axios.get<BranchInfo[]>(`Api/V1/Branch`, {
			headers: {
				'Content-Encoding': 'gzip'
			}
		})
			.then((result) => {
				let defaultBranch: BranchInfo | undefined = result.data.find(b => b.region === getDefaultRegion());
				if (defaultBranch)
				{
					let packingList: PackingList = {
						id: "",
						packingListNo: "PL" + defaultBranch.japanYear + defaultBranch.region + Pad(defaultBranch.plNo, 5),
						createDate: moment(new Date()).format(),
						customerID: 0,
						customerName: "",
						custLangID: 1,
						arrivalDate: "",
						attention: "",
						phone: "",
						fax: "",
						exRate: 1,
						deliveryTo: "C.I.F. HK ",
						deliveryContact: "",
						deliveryAddress: "",
						deliveryPhone: "",
						deliveryFax: "",
						languageID: 1,
						caseMark: "",
						remark: "",
						shipBy: "",
						totalCarton: "",
						totalQty: "",
						totalNetWeight: 0.0,
						totalGrossWeight: 0.0,
						status: "ACT",
						invoiceNo: "",
						createdBy: "",
						loginName: "",
						packingListItems: []
					}
					singleModalRef.current?.Initialise(OpenMode.NEW, packingList);
				}
			},
				(error) => {
					console.log(error);
					showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
				}
			)
			.catch((error) => {
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

	function EditObject(packingListNo: string) {
        setLoading(true);
		axios.get<PackingList>('Api/V1/PackingList/' + packingListNo.replace(/\//g, "%2F"), {
			headers: {
			  'Content-Encoding': 'gzip'
			}
		  })
			.then((result) => {
				result.data.createdBy=''; //createBy cannot be null when save
				result.data.status='ACT'; //status cannot be null when save
				singleModalRef.current?.Initialise(OpenMode.EDIT, result.data);
				setLoading(false);
			},
			  (error) => {
				setLoading(false);
				console.log(error);
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			}
			)
			.catch((error) => {
			  setLoading(false);
			  console.log(error);
			  showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			});
	}

    function DoSearch(event: React.FormEvent<any>) {
		if (event)
        	event.preventDefault();
    
		LoadData();
    }

    return (
        <div className={style.PageMain}>
			<Title pageTitle="Packing Lists" />
			<GenericFilter controls={controls} searchData={searchData} setSearchData={setSearchData} doSearch={DoSearch} searchBoxLabel="Packing List No" />
			<div className={style.PageContent}>
				<DataGridPro
					rows={gridData}
					columns={columns}
					pageSize={searchData.pageSize}
					onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
					pagination
					rowsPerPageOptions={[15, 30, 50, 100]}
					loading={loading}
					checkboxSelection
					components={{
						Toolbar: CustomToolbar,
						Footer: CustomTablePagination
					}}
					componentsProps={{
						footer: { rowsPerPageOptions: [15, 30, 50, 100] }
					}}
					onSelectionModelChange={(selectionModel) => {
						const ids = new Set(selectionModel);
						const selectedRows = gridData.filter((row) => ids.has(row.packingListNo));
						selectedData.current = selectedRows;
					}}
					headerHeight={tinyScreen ? 30 : (smallScreen ? 34 : 38)}
					rowHeight={tinyScreen ? 22 : (smallScreen ? 28 : 34)}
				/>
			</div>

			<PackingListSingle
				ref={singleModalRef}
				customers={props.customers} 
				endUsers={props.endUsers} 
				regions={props.regions}
				sales={props.sales} 
				deliveryMethods={props.deliveryMethods}
				paymentTerms={props.paymentTerms}
				currencies={props.currencies}
				onClose={(saved: boolean, packingList: PackingList) => {
					LoadData();
				} } 
			/>

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

